import { rankItem } from '@tanstack/match-sorter-utils';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'class-names';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Table } from 'react-bootstrap';

import DebouncedInput from '../../lib/Form/DebouncedInput';

import ActionCell from './ActionCell';
import styles from './datatable.module.css';
import TableFooter from './TableFooter';

export const NO_COL_DISPLAY = 'NO_COL_DISPLAY';
export const COLSPAN = '#COLSPAN#';
const PAGE_SIZE_ARR = [10, 20, 50, 100, 200];

const sortingClass = {
  asc: styles.sortingAsc,
  desc: styles.sortingDesc,
};

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({
    itemRank,
  });
  return itemRank.passed;
};

const getNodeText = (node) => {
  if (['string', 'number'].includes(typeof node)) {
    return node === NO_COL_DISPLAY ? '' : node;
  }
  if (node instanceof Array) return node.map(getNodeText).join('');
  if (typeof node === 'object' && node) return getNodeText(node.props.children);
  return '';
};

const DataTable = ({
  data,
  onAction,
  columns,
  children,
  searchable = false,
  paginate = false,
  fallbackContent = 'No data available.',
  searchPlaceholder = 'Search',
  defaultSortColumn = '',
  defaultSortColumnDesc = false,
  lockedRows = {},
  pageSize = 10,
  pageSizeArr = PAGE_SIZE_ARR,
  handleStateChanged,
  tableClassNames = 'table-bordered table-hover',
  renderSubComponent,
  getRowCanExpand = () => false,
  ...props
}) => {
  const [sorting, setSorting] = useState(
    defaultSortColumn
      ? [
          {
            id: defaultSortColumn,
            desc: defaultSortColumnDesc,
          },
        ]
      : [],
  );
  const [globalFilter, setGlobalFilter] = useState('');

  const {
    ids: lockedRowIds = [],
    columnName: lockedRowColumnName = '',
    classNames: lockedRowClassNames = 'bg-a-20-yellow',
  } = lockedRows;

  const table = useReactTable({
    data,
    columns,
    renderSubComponent,
    getRowCanExpand,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    sortingFns: {
      stripTagsSorting: (rowA, rowB, columnId) => {
        return getNodeText(rowA.getValue(columnId)) <
          getNodeText(rowB.getValue(columnId))
          ? -1
          : 1;
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuzzyFilter,
    getPaginationRowModel: paginate ? getPaginationRowModel() : false,
    ...props,
  });

  useEffect(() => {
    if (paginate) {
      table.setPageSize(pageSize);
    }
  }, []);

  useEffect(() => {
    if (typeof handleStateChanged === 'function') {
      handleStateChanged(table.getState());
    }
  }, [table.getState()]);

  return (
    <>
      {searchable ? (
        <Row className="mb-4">
          <Col xs={12} className="d-lg-none pb-2">
            {children}
          </Col>
          <Col md={6} lg={5} xl={4} className="pb-2 pb-md-0">
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => setGlobalFilter(String(value))}
              placeholder={searchPlaceholder}
            />
          </Col>
          <Col className="d-none d-lg-block">{children}</Col>
        </Row>
      ) : (
        <Row>
          <Col>{children}</Col>
        </Row>
      )}

      <Row>
        <Col className="tan-stack-data-table mb-5">
          <Table responsive className={classNames('mb-0', tableClassNames)}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const {
                        rowSpan = 1,
                        width = 0,
                        minWidth = 0,
                        maxWidth = 0,
                        headerClassNames = '',
                        headerButtonClassNames = '',
                      } = header.column.columnDef;
                      return (
                        !(!header.isPlaceholder && rowSpan > 1) && (
                          <th
                            key={header.id}
                            colSpan={header.colSpan}
                            rowSpan={rowSpan}
                            className={classNames(
                              'bg-gray-100 p-0',
                              headerClassNames,
                            )}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            style={{
                              verticalAlign: 'middle',
                              ...(width && { width: `${width}px` }),
                              ...(maxWidth && { maxWidth: `${maxWidth}px` }),
                              ...(minWidth && { minWidth: `${minWidth}px` }),
                            }}
                          >
                            {header.isPlaceholder && rowSpan === 1 ? null : (
                              <Button
                                block
                                variant="link"
                                disabled={!header.column.getCanSort()}
                                onClick={header.column.getToggleSortingHandler()}
                                className={classNames(
                                  'font-weight-bold text-gray text-sm text-sm-left text-uppercase',
                                  {
                                    [styles.noSorting]:
                                      !header.column.getCanSort(),
                                    'text-truncate': !(
                                      header.isPlaceholder && rowSpan > 1
                                    ),
                                  },
                                  styles.headerText,
                                  sortingClass[header.column.getIsSorted()],
                                  headerButtonClassNames,
                                )}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </Button>
                            )}
                          </th>
                        )
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => {
                let isRowLocked = false;
                if (lockedRowIds.length > 0) {
                  const lockRowId = row.original[lockedRowColumnName] || 0;
                  isRowLocked = lockRowId && lockedRowIds.includes(lockRowId);
                }

                return (
                  <>
                    <tr
                      key={row.id}
                      style={
                        typeof props?.getRowStyles === 'function'
                          ? props?.getRowStyles(row)
                          : {}
                      }
                      className={classNames({
                        [lockedRowClassNames]: isRowLocked,
                        'bg-a-10-blue': row.getIsExpanded(),
                      })}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const {
                          width = 0,
                          minWidth = 0,
                          maxWidth = 0,
                        } = cell.column.columnDef;

                        const {
                          colSpan = 1,
                          colSpanClassName = '',
                          actions = [],
                          className = '',
                          actionButtonClass = 'mb-0 ml-1 p-0 text-gray',
                          actionIconClass = '',
                        } = cell.column.columnDef;

                        let isColSpan = false;
                        let customColSpan = 1;
                        let isCustomColSpan = false;
                        if (colSpan > 1) {
                          if (
                            cell.getValue() &&
                            cell.getValue().startsWith(COLSPAN)
                          ) {
                            customColSpan = colSpan;
                            isCustomColSpan = true;
                            isColSpan = true;
                          }
                        }

                        return (
                          cell.getValue() !== NO_COL_DISPLAY && (
                            <td
                              className={classNames(
                                className,
                                isColSpan ? colSpanClassName : '',
                              )}
                              {...{
                                key: cell.id,
                                style: {
                                  ...(width && { width: `${width}px` }),
                                  ...(maxWidth && {
                                    maxWidth: `${maxWidth}px`,
                                  }),
                                  ...(minWidth && {
                                    minWidth: `${minWidth}px`,
                                  }),
                                },
                                colSpan: customColSpan,
                              }}
                            >
                              {/* eslint-disable-next-line no-nested-ternary */}
                              {typeof cell.getValue() !== 'object'
                                ? flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )
                                : isCustomColSpan
                                  ? cell.getValue().replace(COLSPAN, '')
                                  : cell.getValue()}

                              <ActionCell
                                isRowLocked={isRowLocked}
                                cell={cell}
                                row={row}
                                actions={actions}
                                onAction={onAction}
                                buttonClass={actionButtonClass}
                                iconClass={actionIconClass}
                              />
                            </td>
                          )
                        );
                      })}
                    </tr>
                    {row.getIsExpanded() && (
                      <tr key={`child_${row.id}`}>
                        <td
                          colSpan={row.getVisibleCells().length}
                          className={styles.bottomShadow}
                        >
                          {renderSubComponent({ row })}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {table.getRowModel().rows.length === 0 && (
                <tr>
                  <td
                    colSpan={table.getAllFlatColumns().length}
                    className="text-center"
                  >
                    {fallbackContent}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {paginate && data.length > pageSize && (
            <TableFooter table={table} pageSizeArr={pageSizeArr} />
          )}
        </Col>
      </Row>
    </>
  );
};

DataTable.propTypes = {
  children: PropTypes.node,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessorKey: PropTypes.string,
      header: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    }),
  ),
  data: PropTypes.arrayOf(PropTypes.shape({})),
  defaultSortColumn: PropTypes.string,
  defaultSortColumnDesc: PropTypes.bool,
  fallbackContent: PropTypes.node,
  getRowCanExpand: PropTypes.func,
  getRowStyles: PropTypes.func,
  handleStateChanged: PropTypes.func,
  lockedRows: PropTypes.shape({
    ids: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.number),
      PropTypes.arrayOf(PropTypes.string),
    ]),
    columnName: PropTypes.string,
    classNames: PropTypes.string,
  }),
  onAction: PropTypes.func,
  pageSize: PropTypes.number,
  pageSizeArr: PropTypes.arrayOf(PropTypes.number),
  paginate: PropTypes.bool,
  renderSubComponent: PropTypes.func,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  tableClassNames: PropTypes.string,
};
export default DataTable;
